import {addSortToQuery, getSortFromQuery} from '@github-ui/dashboard-sorting-dropdown/query'
import {hpcAttrs} from '@github-ui/hpc'
import {addIssueFieldToQuery, createReplaceIssueFieldQuery} from '@github-ui/issue-url-helper'
import type {
  RepoIssuesDashboardPageInfo,
  RepoIssuesDashboardResult,
} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Link as PreloadableLink, Link as RouterLink} from '@github-ui/react-router'
import {IssuesList} from '@github-ui/shared-dashboard-lists/issues/IssuesList'
import type {IssueDeferredItem} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
import type {DeferredItem} from '@github-ui/shared-dashboard-lists/pulls/types/list-types'
import type {EmptyListState} from '@github-ui/shared-dashboard-lists/shared/types/blankslate-types'
import type {FilterSelectType} from '@github-ui/shared-dashboard-lists/shared/types/list-types'
import {
  addAssigneeFilterToQuery,
  addAuthorFilterToQuery,
  addBlockedFilterToQuery,
  addLabelFilterToQuery,
  addParentIssueFilterToQuery,
  addRepoFilterToQuery,
  addTypeFilterToQuery,
} from '@github-ui/shared-dashboard-surface/helpers/query-filters'
import {addStateToQuery, getStateFromQuery} from '@github-ui/shared-dashboard-surface/helpers/open-closed-toggle'
import {Pagination} from '@primer/react'
import {type ComponentProps, useMemo, useState} from 'react'

import {repoIssuesToListItems} from '../helpers/repo-issues-dashboard-proto'
import {mergeDeferredIssues} from '../helpers/merge-deferred-issues'

type IssuesListProps = ComponentProps<typeof IssuesList>
export type IssuesListSecondaryResult = IssueDeferredItem | (DeferredItem & {itemType: 'pull_request'})
const EMPTY_SECONDARY_RESULTS: IssuesListSecondaryResult[] = []

export interface IssuesListRouteAdapter {
  readonly scopeIdentity: string
  readonly query: string
  readonly page: number
  readonly fetch: (input: {query: string; page?: number}) => void
  readonly getPageHref: (page: number) => string
  readonly getQueryHref: (query: string, page?: number) => string
}

export interface IssuesListSurfaceProps {
  readonly response: {
    readonly results?: RepoIssuesDashboardResult[]
    readonly pageInfo?: RepoIssuesDashboardPageInfo
    readonly error?: string
  }
  readonly routeAdapter: IssuesListRouteAdapter
  readonly secondaryResults?: IssuesListSecondaryResult[]
  readonly filterActions?: IssuesListProps['filterActions']
  readonly defaultSortingKey?: IssuesListProps['defaultSortingKey']
  readonly isLoading?: boolean
}

export function IssuesListSurface({
  response,
  routeAdapter,
  secondaryResults = EMPTY_SECONDARY_RESULTS,
  filterActions,
  defaultSortingKey = 'created',
  isLoading = false,
}: IssuesListSurfaceProps) {
  const [compactMode, setCompactMode] = useState(false)
  const issues = useMemo(
    () => mergeDeferredIssues(repoIssuesToListItems(response.results ?? []), secondaryResults),
    [response.results, secondaryResults],
  )
  const pageInfo = response.pageInfo ?? {currentPage: 1, totalPages: 1, totalCount: 0}

  const handleFilterSelect = (type: FilterSelectType, value: string) => {
    switch (type) {
      case 'repo':
        return routeAdapter.fetch({query: addRepoFilterToQuery(routeAdapter.query, value)})
      case 'author':
        return routeAdapter.fetch({query: addAuthorFilterToQuery(routeAdapter.query, value)})
      case 'label':
        return routeAdapter.fetch({query: addLabelFilterToQuery(routeAdapter.query, value)})
      case 'assignee':
        return routeAdapter.fetch({query: addAssigneeFilterToQuery(routeAdapter.query, value)})
      default: {
        const _exhaustive: never = type
        return _exhaustive
      }
    }
  }

  const handleIssueFieldSelect = (fieldName: string, queryValue: string) => {
    const query =
      createReplaceIssueFieldQuery(routeAdapter.query, fieldName, queryValue) ??
      addIssueFieldToQuery(routeAdapter.query, fieldName, queryValue)
    routeAdapter.fetch({query})
  }

  const emptyListState: EmptyListState = {
    dashboardType: 'issues',
    filtered: routeAdapter.query.length > 0,
    currentPage: pageInfo.currentPage,
    pageCount: pageInfo.totalPages,
    errorMessage: response.error || undefined,
  }

  return (
    <div className="tmp-my-3" data-testid="issues-list-surface" data-scope-identity={routeAdapter.scopeIdentity}>
      <div {...hpcAttrs()}>
        <IssuesList
          issues={issues}
          pageInfo={pageInfo}
          compactMode={compactMode}
          onCompactModeChange={setCompactMode}
          defaultSortingKey={defaultSortingKey}
          onSortingItemSelect={sort => routeAdapter.fetch({query: addSortToQuery(routeAdapter.query, sort)})}
          onStateFilterSelect={state => routeAdapter.fetch({query: addStateToQuery(routeAdapter.query, state)})}
          sortingItemSelected={getSortFromQuery(routeAdapter.query)}
          stateItemSelected={getStateFromQuery(routeAdapter.query)}
          emptyListState={emptyListState}
          onFilterSelect={handleFilterSelect}
          onIssueFieldSelect={handleIssueFieldSelect}
          filterActions={filterActions}
          isLoading={isLoading}
          isAgentAttributionEnabled={isFeatureEnabled('agent_author_search_expansion_ui_pulls')}
          getIssueTypeHref={issueTypeName =>
            routeAdapter.getQueryHref(addTypeFilterToQuery(routeAdapter.query, issueTypeName))
          }
          getIssueBlockedHref={() => routeAdapter.getQueryHref(addBlockedFilterToQuery(routeAdapter.query))}
          getParentIssueHref={(repoNameWithOwner, number) =>
            routeAdapter.getQueryHref(addParentIssueFilterToQuery(routeAdapter.query, repoNameWithOwner, number))
          }
          issueTypeLinkComponent={RouterLink}
        />
        {pageInfo.totalPages > 1 && (
          <Pagination
            pageCount={pageInfo.totalPages}
            currentPage={pageInfo.currentPage}
            hrefBuilder={routeAdapter.getPageHref}
            renderPage={({key, number, href, ...props}) => (
              <PreloadableLink key={key} {...props} to={href ?? routeAdapter.getPageHref(number)} preload="intent" />
            )}
            onPageChange={(event, page) => {
              event.preventDefault()
              routeAdapter.fetch({query: routeAdapter.query, page})
            }}
          />
        )}
      </div>
    </div>
  )
}
